import React, { useEffect, useState } from 'react'
import { all_routes } from '../../../router/all_routes'
import { Link, useSearchParams } from 'react-router-dom'
import { PermissionType, User, UserCreateDTO, UserStatus, UserUpdateDTO } from '../../../types/user.types';
import dayjs from 'dayjs';
import { useEmployee } from '../../../hooks/useEmployee';
import EmployeeTable from './components/EmployeeTable';
import EmployeeFilters from './components/EmployeeFilters';
import { handleEmployeeExportExcel, createPdf, normalizeTurkishChars } from '../../../utils/exportUtils';
import LoadingSpinner from '../../shared/uiInterface/base-ui/LoadingSpinner';
import PersonalInfoStep from './components/form-steps/PersonalInfoStep';
import ContactInfoStep from './components/form-steps/ContactInfoStep';
import WorkInfoStep from './components/form-steps/WorkInfoStep';
import SecurityStep from './components/form-steps/SecurityStep';
import LeaveLimitsModal from './components/LeaveLimitsModal';
import EmployeeBulkImportModal from './components/EmployeeBulkImportModal';
import EmployeeBulkEditModal from './components/EmployeeBulkEditModal';
import { BulkUpdateResult } from '../../../types/userBulk.types';
import { Form, Modal, message } from 'antd';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';
import { AnnualLeaveLimitResponse } from '../../../types/company.types';
import { useTranslation } from 'react-i18next';
import companyService from '../../../services/company/company.service';
import { handleApiError } from '../../../utils/errorHandler';
import FooterSection from '../../shared/components/footerSection';
import userService from '../../../services/user/user.service';
import { RolesEnum } from '../../../types/route.types';
type PasswordField = "password" | "confirmPassword";

const EmployeeList: React.FC = () => {
  const { t } = useTranslation();
  const {
    users,
    departments,
    shops,
    loading,
    error,
    selectedDepartment,
    selectedShop,
    selectedStatus,
    selectedBloodType,
    handleDepartmentChange,
    handleShopChange,
    handleStatusChange,
    handleBloodTypeChange,
    resetFilters,
    fetchUsers,
    fetchDepartmentsAndShops,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    updateEmployeeStatus
  } = useEmployee();
  const [searchParams] = useSearchParams();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<'personal' | 'contact' | 'work' | 'security'>('personal');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false);
  const [isLeaveLimitsModalOpen, setIsLeaveLimitsModalOpen] = useState(false);
  const [selectedUserForLeaveLimits, setSelectedUserForLeaveLimits] = useState<User | null>(null);
  const [annualLimit, setAnnualLimit] = useState<AnnualLeaveLimitResponse | null>(null);
  const { companyId } = useSelector((state: RootState) => state.auth);
  const { sub, user } = useSelector((state: RootState) => state.auth);
  // Form instance'ı oluştur
  const [form] = Form.useForm();
  const [initialFiltersApplied, setInitialFiltersApplied] = useState(false);
  const [formErrors, setFormErrors] = useState<string[]>([]); // Form hatalarını tutmak için state
  const [activeUsers, setActiveUsers] = useState<number>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [bulkImportRemainingSlots, setBulkImportRemainingSlots] = useState<number | undefined>();
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const canEditUsers = !!user?.permissions?.includes(PermissionType.EDIT_USERS);

  // Liste yenilenince artık görünmeyen (silinmiş vb.) çalışanları seçimden çıkar
  useEffect(() => {
    setSelectedEmployeeIds(prev => prev.filter(id => users.some(u => u.id === id)));
  }, [users]);

  const handleBulkEditDone = async (result: BulkUpdateResult) => {
    await fetchUsers();
    // Başarısız olanlar seçili kalsın ki düzeltip tekrar denenebilsin
    setSelectedEmployeeIds(result.results.filter(r => !r.success).map(r => r.id));
  };




  useEffect(() => {
    const fetchAnnualLimit = async () => {
      if (companyId) {
        const response = await companyService.getAnnualLimit(Number(companyId));
        setAnnualLimit(response);
      }
    };
    fetchAnnualLimit();
  }, [companyId]);

  const fetchActiveUsers = async () => {
    const response = await companyService.getCompanyActiveUsers(Number(companyId));
    setActiveUsers(response.activeUserCount);
    return response.activeUserCount;
  };

  // Component mount olduğunda verileri çek
  const fetchData = async () => {
    await Promise.all([
      fetchUsers(),
      fetchDepartmentsAndShops()
    ]);
  };

  // İlk veri yüklemesi
  useEffect(() => {
    fetchData();
  }, []);

  // URL parametrelerinden gelen departman veya şube ID'sine göre filtreleme yap
  useEffect(() => {
    const departmentParam = searchParams.get('department');
    const branchParam = searchParams.get('branch');

    // Veriler yüklenmişse ve henüz ilk filtreleme uygulanmamışsa
    if (departments.length > 0 && shops.length > 0 && !loading && !initialFiltersApplied) {

      // İlk olarak, filtreleri varsayılan değerlere sıfırla
      resetFilters();

      let filtersChanged = false;

      // Önce şube parametresini kontrol et ve uygula
      if (branchParam && shops.some(shop => shop.id.toString() === branchParam)) {
        setTimeout(() => {
          handleShopChange(branchParam);
        }, 0);
        filtersChanged = true;
      }

      // Sonra departman parametresini kontrol et ve uygula
      if (departmentParam && departments.some(dept => dept.id.toString() === departmentParam)) {
        setTimeout(() => {
          handleDepartmentChange(departmentParam);
        }, 0);
        filtersChanged = true;
      }

      // Filtrelerin uygulandığını işaretle (tekrar tekrar uygulanmasını önlemek için)
      if (filtersChanged || (!departmentParam && !branchParam)) {
        setInitialFiltersApplied(true);
      }
    }
  }, [departments, shops, loading, searchParams, handleDepartmentChange, handleShopChange, resetFilters, initialFiltersApplied]);

  const showModal = async () => {
    resetForm();
    const currentActiveUsers = await fetchActiveUsers();
    if (currentActiveUsers && currentActiveUsers >= Number(sub?.userLimit)) {
      setIsLimitModalOpen(true);
      return;
    }
    setIsModalOpen(true);
  };

  const showBulkImportModal = async () => {
    const currentActiveUsers = await fetchActiveUsers();
    const userLimit = Number(sub?.userLimit);
    if (currentActiveUsers && currentActiveUsers >= userLimit) {
      setIsLimitModalOpen(true);
      return;
    }
    setBulkImportRemainingSlots(
      Number.isFinite(userLimit) && typeof currentActiveUsers === 'number' ? userLimit - currentActiveUsers : undefined
    );
    setIsBulkImportOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    resetForm();
  };
  const handleStatusChangeTable = async (id: number, status: UserStatus) => {
    try {
      // userService'in updateUserStatus metodunu kullan
      await updateEmployeeStatus(id, status);

      // Başarılı olursa kullanıcı listesini güncelle
      await fetchUsers();

      // UX için bildirim gösteriliyor (EmployeeTable içinde de gösteriliyor)
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const handleDeviceReset = async (id: number) => {
    try {
      await userService.resetDevice(id);
      await fetchUsers();
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const handleLeaveLimits = (user: User) => {
    setSelectedUserForLeaveLimits(user);
    setIsLeaveLimitsModalOpen(true);
  };

  const handleLeaveLimitsSuccess = async () => {
    await fetchUsers(); // Kullanıcı listesini yenile
  };


  const handleEdit = (user: User) => {

    form.resetFields();
    setSelectedUser(user);

    // Form değerlerini doğru şekilde set et
    const formValues = {
      ...user,
      departmentId: user.department?.map(d => d.id), // department ID'sini set et
      branchId: user.branch?.map(b => b.id), // branch ID'sini set et
      primaryDepartmentId: user.primaryDepartment?.id, // ana departman ID'sini set et
      birthDate: user.birthDate ? dayjs(user.birthDate) : null,
      hireDate: user.hireDate ? dayjs(user.hireDate) : null,
      terminationDate: user.terminationDate ? dayjs(user.terminationDate) : null,
      role: user.role || RolesEnum.USER,
    };

    form.setFieldsValue(formValues);

    setIsModalOpen(true);
  };
  const handleExportPdf = () => {
    // Dışa aktarım için özelleştirilmiş kullanıcı verileri oluştur
    const formattedUsers = users.map(user => ({
      firstName: normalizeTurkishChars(user.firstName) || '-',
      lastName: normalizeTurkishChars(user.lastName) || '-',
      email: normalizeTurkishChars(user.email) || '-',
      department: user.department?.map(d => normalizeTurkishChars(d.name)).join(', ') || '-',
      branch: user.branch?.map(b => normalizeTurkishChars(b.name)).join(', ') || '-',
      phone: user.phone || '-',
      status: normalizeTurkishChars(user.status === 'active' ? 'Aktif' : 'Pasif'),
      role: normalizeTurkishChars(user.role === 'ADMIN' ? 'Yonetici' : 'Kullanici'),
      hireDate: normalizeTurkishChars(user.hireDate ? dayjs(user.hireDate).format('DD.MM.YYYY') : '-'),
      title: normalizeTurkishChars(user.title || '-')
    }));

    // PDF için sütun tanımları - Landscape A4 genişliğine göre optimize edildi (297mm, margin'lerle ~281mm)
    const columns = [
      { header: normalizeTurkishChars('Ad'), property: 'firstName' as keyof typeof formattedUsers[0], width: 26, align: 'left' as const },
      { header: normalizeTurkishChars('Soyad'), property: 'lastName' as keyof typeof formattedUsers[0], width: 26, align: 'left' as const },
      { header: normalizeTurkishChars('E-posta'), property: 'email' as keyof typeof formattedUsers[0], width: 42, align: 'left' as const },
      { header: normalizeTurkishChars('Departman'), property: 'department' as keyof typeof formattedUsers[0], width: 32, align: 'left' as const },
      { header: normalizeTurkishChars('Şube'), property: 'branch' as keyof typeof formattedUsers[0], width: 26, align: 'left' as const },
      { header: normalizeTurkishChars('Pozisyon'), property: 'title' as keyof typeof formattedUsers[0], width: 32, align: 'left' as const },
      { header: normalizeTurkishChars('Telefon'), property: 'phone' as keyof typeof formattedUsers[0], width: 26, align: 'left' as const },
      { header: normalizeTurkishChars('İşe Başlama'), property: 'hireDate' as keyof typeof formattedUsers[0], width: 26, align: 'center' as const },
      { header: normalizeTurkishChars('Durum'), property: 'status' as keyof typeof formattedUsers[0], width: 22, align: 'center' as const },
      { header: normalizeTurkishChars('Rol'), property: 'role' as keyof typeof formattedUsers[0], width: 22, align: 'center' as const }
    ];

    // İstatistikler
    const statistics = [
      { label: normalizeTurkishChars('Toplam Calisan'), value: users.length, color: '#3b82f6' }, // Mavi
      { label: normalizeTurkishChars('Aktif Calisan'), value: users.filter(u => u.status === 'active').length, color: '#10b981' }, // Yeşil
      { label: normalizeTurkishChars('Pasif Calisan'), value: users.filter(u => u.status !== 'active').length, color: '#f59e0b' } // Turuncu
    ];

    // Açıklama metni
    let description = normalizeTurkishChars('Tum calisanlarin listesi');
    if (selectedDepartment !== 'all' && departments.length > 0) {
      const deptName = departments.find(d => d.id.toString() === selectedDepartment)?.name;
      description += ` - ${normalizeTurkishChars('Departman')}: ${deptName}`;
    }
    if (selectedShop !== 'all' && shops.length > 0) {
      const shopName = shops.find(s => s.id.toString() === selectedShop)?.name;
      description += ` - ${normalizeTurkishChars('Sube')}: ${shopName}`;
    }

    // createPdf fonksiyonunu çağır
    createPdf(
      formattedUsers,
      {
        title: normalizeTurkishChars('Calisan Listesi'),
        companyName: normalizeTurkishChars('DINOX HR'),
        headerColor: '#2c3e50', // Koyu lacivert
        accentColor: '#3498db', // Mavi
        description: description,
        columns: columns,
        showStatistics: true,
        statistics: statistics,
        footer: {
          text: `© ${new Date().getFullYear()} ${normalizeTurkishChars('DINOX')} - ${normalizeTurkishChars('Tum Haklari Saklidir')}`,
          showPageNumbers: true
        }
      }
    );
  };
  const handleFormSubmit = async () => {
    if (isSubmitting) return; // Çift tıklamayı engelle
    setFormErrors([]);
    setIsSubmitting(true);
    try {
      const values = await form.validateFields();

      const baseFormData = {
        ...values,
        department: values.departmentId,
        branch: values.branchId,
        primaryDepartmentId: values.primaryDepartmentId,
        birthDate: values.birthDate ? dayjs(values.birthDate).format('YYYY-MM-DD') : undefined,
        hireDate: values.hireDate ? dayjs(values.hireDate).format('YYYY-MM-DD') : new Date().toISOString(),
        terminationDate: values.terminationDate ? dayjs(values.terminationDate).format('YYYY-MM-DD') : null,
        role: values.role ? values.role : RolesEnum.USER,
      };

      // CompanyId'yi hem normal kullanıcı hem de impersonation durumu için al
      const effectiveCompanyId = user?.companyId || (user as any)?.company?.id;
      
      if (!user || typeof effectiveCompanyId !== 'number') {
        throw new Error("Mevcut kullanıcının şirket kimliği bulunamadı.");
      }

      if (selectedUser) {
        // Update işlemi için email'i çıkar
        const { email, password, ...updateData } = baseFormData;
        const formData: UserUpdateDTO = updateData;
        await updateEmployee(selectedUser.id, formData);
      } else {
        // Create işlemi için email'i dahil et
        const createData: UserCreateDTO = {
          ...baseFormData,
          companyId: effectiveCompanyId,
          password: values.password,
        };
        await createEmployee(createData);
      }
      setIsModalOpen(false);
      resetForm();
      await fetchUsers();
    } catch (errorInfo: any) {
      // handleApiError(error,t); // Bu satırı yorumladım, çünkü Ant Design'ın kendi hata yönetimini kullanacağız
      if (errorInfo.errorFields && errorInfo.errorFields.length > 0) {
        const errorMessages = errorInfo.errorFields.map((field: any) => field.errors.join(', '));
        setFormErrors(errorMessages); // Hata mesajlarını state'e kaydet
        message.error(t('employees.validationErrors.fillRequiredFields')); // Genel hata mesajı

        const firstErrorField = errorInfo.errorFields[0].name.join('.'); // AntD field path'ini al

        // Hangi sekmede hata olduğunu belirle
        // Bu kısım form alanlarının hangi sekmede olduğuna göre özelleştirilmeli
        // Örnek: PersonalInfoStep alanları
        const personalInfoFields = ['firstName', 'lastName', 'birthDate', 'nationalId', 'gender', 'maritalStatus', 'educationLevel'];
        // Örnek: ContactInfoStep alanları
        const contactInfoFields = ['city', 'district', 'address', 'email', 'phone', 'emergencyContact'];
        // Örnek: WorkInfoStep alanları
        const workInfoFields = ['departmentId', 'branchId', 'primaryDepartmentId', 'annualLeaveLimit', 'isFixedShift', 'hireDate', 'role'];
        // Örnek: SecurityStep alanları
        const securityInfoFields = ['password', 'title', 'isFaceIdAllow'];

        let errorTab = activeTab; // Varsayılan olarak mevcut tab

        if (personalInfoFields.some(field => firstErrorField.startsWith(field))) {
          errorTab = 'personal';
        } else if (contactInfoFields.some(field => firstErrorField.startsWith(field))) {
          errorTab = 'contact';
        } else if (workInfoFields.some(field => firstErrorField.startsWith(field))) {
          errorTab = 'work';
        } else if (securityInfoFields.some(field => firstErrorField.startsWith(field))) {
          errorTab = 'security';
        }

        setActiveTab(errorTab); // Hatalı sekmeye geç

        // Hatalı alana scroll yap (kısa bir gecikmeyle daha iyi çalışabilir)
        setTimeout(() => {
          form.scrollToField(firstErrorField, {
            behavior: 'smooth',
            block: 'center', // Ortalamak daha iyi olabilir
          });
        }, 100);

      } else {
        // console.log(errorInfo);
        // API'den gelen veya beklenmedik bir hata ise
        handleApiError(errorInfo, t);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedUser(null);
    setActiveTab('personal');
    form.resetFields();
    form.setFieldsValue({
      firstName: '',
      lastName: '',
      birthDate: null,
      nationalId: '',
      gender: undefined,
      maritalStatus: undefined,
      educationLevel: undefined,
      email: '',
      phone: '',
      address: '',
      city: '',
      title: '',
      emergencyContact: '',
      departmentId: undefined,
      branchId: undefined,
      primaryDepartmentId: undefined,
      hireDate: null,
      terminationDate: null,
      position: '',
      username: '',
      password: '',
    });
  };

  // Modal içeriğini düzenleyelim
  const renderModalContent = () => {
    return (
      <Form
        form={form}
        layout="vertical"
        initialValues={selectedUser || {}}
      >
        <div className="contact-grids-tab">
          <ul className="nav nav-underline" id="myTab" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'personal' ? 'active' : ''}`}
                onClick={() => setActiveTab('personal')}
                type="button"
                role="tab"
              >
                {t('employees.modals.personal')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'contact' ? 'active' : ''}`}
                onClick={() => setActiveTab('contact')}
                type="button"
                role="tab"
              >
                {t('employees.modals.contact')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'work' ? 'active' : ''}`}
                onClick={() => setActiveTab('work')}
                type="button"
              >
                {t('employees.modals.work')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'security' ? 'active' : ''}`}
                onClick={() => setActiveTab('security')}
                type="button"
                role="tab"
              >
                {t('employees.modals.security')}
              </button>
            </li>
          </ul>
        </div>
        <div className="modal-body">
          {/* Tüm form alanlarını her zaman render et, sadece görünürlüğü değiştir */}
          <div style={{ display: activeTab === 'personal' ? 'block' : 'none' }}>
            <PersonalInfoStep form={form} />
          </div>
          <div style={{ display: activeTab === 'contact' ? 'block' : 'none' }}>
            <ContactInfoStep form={form} />
          </div>
          <div style={{ display: activeTab === 'work' ? 'block' : 'none' }}>
            <WorkInfoStep 
              form={form} 
              departments={departments} 
              shops={shops} 
              annualLimit={annualLimit?.annualLeaveLimit || 0} 
              isEdit={!!selectedUser} 
              userId={selectedUser?.id}
            />
          </div>
          <div style={{ display: activeTab === 'security' ? 'block' : 'none' }}>
            <SecurityStep form={form} isEdit={!!selectedUser} />
          </div>
        </div>
      </Form>
    );
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">
                {t('employees.pageTitle')}
                {selectedDepartment !== 'all' && departments.length > 0 && (
                  <> - {departments.find(d => d.id.toString() === selectedDepartment)?.name}</>
                )}
                {selectedShop !== 'all' && shops.length > 0 && (
                  <> - {shops.find(s => s.id.toString() === selectedShop)?.name}</>
                )}
              </h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">{t('employees.breadcrumb.employee')}</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {t('employees.breadcrumb.employees')}
                  </li>
                </ol>
              </nav>
            </div>

            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              {user?.permissions?.includes(PermissionType.EDIT_USERS) && (
              <button
                className="btn btn-sm btn-primary d-inline-flex align-items-center me-2"
                onClick={showModal}
                data-tour="add-employee"
              >
                <i className="ti ti-plus me-1" />
                  {t('employees.addEmployee')}
                </button>
              )}
              {user?.permissions?.includes(PermissionType.EDIT_USERS) && (
              <button
                className="btn btn-sm btn-white d-inline-flex align-items-center me-2"
                onClick={showBulkImportModal}
              >
                <i className="ti ti-file-spreadsheet me-1" />
                Excel ile Ekle
              </button>
              )}
              {user?.permissions?.includes(PermissionType.EXPORT) && (
              <div className="dropdown me-3">
                <Link
                  to="#"
                  className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                  data-bs-toggle="dropdown"
                >
                  <i className="ti ti-file-export me-1" />
                  {t('common.buttons.export')}
                </Link>
                <ul className="dropdown-menu dropdown-menu-end p-3">
                  <li>
                    <Link
                      to="#"
                      className="dropdown-item rounded-2 d-flex align-items-center py-2"
                      onClick={() => handleExportPdf()}
                    >
                      <i className="ti ti-file-type-pdf me-2 text-danger" />
                      {t('common.buttons.pdf')}
                    </Link>
                  </li>
                  <li className="mt-1">
                    <Link
                      to="#"
                      className="dropdown-item rounded-2 d-flex align-items-center py-2"
                      onClick={() => handleEmployeeExportExcel(users)}
                    >
                      <i className="ti ti-file-type-xls me-2 text-success" />
                      {t('common.buttons.excel')}
                    </Link>
                  </li>
                </ul>
              </div>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-end flex-wrap row-gap-3">
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">

                <EmployeeFilters
                  departments={departments}
                  shops={shops}
                  selectedDepartment={selectedDepartment}
                  selectedShop={selectedShop}
                  selectedStatus={selectedStatus}
                  selectedBloodType={selectedBloodType}
                  onDepartmentChange={handleDepartmentChange}
                  onShopChange={handleShopChange}
                  onStatusChange={handleStatusChange}
                  onBloodTypeChange={handleBloodTypeChange}
                  onReset={resetFilters}
                />
              </div>
            </div>

            {/* Employee Table */}
            <div className="card-body">
              {loading ? (
                <LoadingSpinner size="lg" color="primary" centered />
              ) : (
                <div data-tour="employees-table">
                  {selectedEmployeeIds.length > 0 && (
                    <div className="alert alert-primary d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                      <span>
                        <strong>{selectedEmployeeIds.length}</strong> çalışan seçildi
                      </span>
                      <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-primary" onClick={() => setIsBulkEditOpen(true)}>
                          <i className="ti ti-edit me-1" />
                          Toplu Değiştir
                        </button>
                        <button className="btn btn-sm btn-white" onClick={() => setSelectedEmployeeIds([])}>
                          Seçimi Temizle
                        </button>
                      </div>
                    </div>
                  )}
                  <EmployeeTable
                    selectedIds={selectedEmployeeIds}
                    onSelectionChange={canEditUsers ? setSelectedEmployeeIds : undefined}
                    users={users || []}
                    currentUser={user as User}
                    onEdit={handleEdit}
                    onDelete={deleteEmployee}
                    onStatusChange={handleStatusChangeTable}
                    onDeviceReset={handleDeviceReset}
                    onLeaveLimits={handleLeaveLimits}
                  />
                </div>
              )}
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error.message}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <FooterSection />
        </div>
      </div>

      <EmployeeBulkImportModal
        open={isBulkImportOpen}
        onClose={() => setIsBulkImportOpen(false)}
        onImported={fetchUsers}
        departments={departments}
        shops={shops}
        existingUsers={users || []}
        companyId={user?.companyId || (user as any)?.company?.id}
        remainingSlots={bulkImportRemainingSlots}
      />

      <EmployeeBulkEditModal
        open={isBulkEditOpen}
        onClose={() => setIsBulkEditOpen(false)}
        onDone={handleBulkEditDone}
        selectedUsers={users.filter(u => selectedEmployeeIds.includes(u.id))}
        departments={departments}
        shops={shops}
        currentUser={user as User}
      />

      <Modal
        title={
          <div className="d-flex align-items-center">
            <h4 className="modal-title me-2 mb-0">
              {selectedUser ? "Çalışan Düzenle" : "Çalışan Ekle"}
            </h4>
          </div>
        }
        open={isModalOpen}
        onCancel={handleCancel}
        width={800}
        footer={[
          <button
            key="cancel"
            type="button"
            className="btn btn-outline-light border me-2"
            onClick={handleCancel}
          >
            İptal Et
          </button>,
          <button
            key="submit"
            type="button"
            onClick={handleFormSubmit}
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        ]}
      >
        {renderModalContent()}
      </Modal>

      {/* Kullanıcı Limiti Aşım Modal'ı */}
      <Modal
        title={
          <div className="d-flex align-items-center">
            <i className="ti ti-alert-triangle text-warning me-2" style={{ fontSize: '24px' }}></i>
            <h4 className="modal-title me-2 mb-0 text-warning">
              Kullanıcı Limiti Aşıldı
            </h4>
          </div>
        }
        open={isLimitModalOpen}
        onCancel={() => setIsLimitModalOpen(false)}
        width={600}
        footer={[
          <button
            key="cancel"
            type="button"
            className="btn btn-outline-light border me-2"
            onClick={() => setIsLimitModalOpen(false)}
          >
            Kapat
          </button>,
          <Link
            key="upgrade"
            to={`${all_routes.companySettings}?tab=payment-settings`}
            className="btn btn-primary"
            onClick={() => setIsLimitModalOpen(false)}
          >
            <i className="ti ti-credit-card me-1"></i>
            Planı Yükselt
          </Link>
        ]}
        centered
      >
        <div className="text-center py-3">
          <div className="mb-4">
            <i className="ti ti-users text-primary" style={{ fontSize: '64px' }}></i>
          </div>

          <h5 className="mb-3">Çalışan Sayısı Limitini Aştınız!</h5>

          <div className="alert alert-warning" role="alert">
            <div className="d-flex align-items-center">
              <i className="ti ti-info-circle me-2"></i>
              <div>
                <strong>Mevcut Limit:</strong> {sub?.userLimit} çalışan<br />
                <strong>Aktif Çalışan:</strong> {activeUsers} çalışan
              </div>
            </div>
          </div>

          <p className="text-muted mb-4">
            Daha fazla çalışan eklemek için planınızı yükseltmeniz gerekmektedir.
            Şirket ayarlarından kolayca kullanıcı limitinizi artırabilirsiniz.
          </p>

          {/* <div className="border rounded p-3 bg-light">
            <h6 className="text-primary mb-2">
              <i className="ti ti-rocket me-1"></i>
              Plan Yükseltme Avantajları:
            </h6>
            <ul className="list-unstyled text-start">
              <li><i className="ti ti-check text-success me-2"></i>Sınırsız çalışan ekleme</li>
              <li><i className="ti ti-check text-success me-2"></i>Gelişmiş raporlama</li>
              <li><i className="ti ti-check text-success me-2"></i>7/24 destek</li>
              <li><i className="ti ti-check text-success me-2"></i>%20 yıllık ödeme indirimi</li>
            </ul>
          </div> */}
        </div>
      </Modal>

      {/* İzin Limitleri Modalı */}
      <LeaveLimitsModal
        open={isLeaveLimitsModalOpen}
        onClose={() => {
          setIsLeaveLimitsModalOpen(false);
          setSelectedUserForLeaveLimits(null);
        }}
        user={selectedUserForLeaveLimits}
        onSuccess={handleLeaveLimitsSuccess}
      />
    </>
  );
};

export default EmployeeList;
